import React, { useMemo, useState } from 'react';
import {
  Box, Chip, Paper, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { buildNodeInspection } from '../../utils/studioV2NodeInspector';

function JsonBlock({ value, emptyLabel = 'No data' }) {
  if (value == null || (typeof value === 'object' && !Object.keys(value).length)) {
    return <Typography variant="body2" color="text.disabled">{emptyLabel}</Typography>;
  }
  return (
    <Typography
      component="pre"
      variant="caption"
      sx={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        fontFamily: 'monospace',
        fontSize: 11,
        m: 0,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Typography>
  );
}

export default function StudioV2NodeInspector({
  nodeId,
  events = [],
  runPayload = null,
}) {
  const [tab, setTab] = useState(0);
  const inspection = useMemo(
    () => buildNodeInspection({ events, runPayload, nodeId }),
    [events, runPayload, nodeId],
  );

  if (!nodeId) {
    return (
      <Typography variant="body2" color="text.disabled">
        Select a node in the trace timeline to inspect inputs, outputs, variables, and stream events.
      </Typography>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" fontWeight={700}>Node inspector</Typography>
          <Chip size="small" label={nodeId} sx={{ fontFamily: 'monospace' }} />
          {Number.isFinite(inspection.duration) && (
            <Chip size="small" variant="outlined" label={`${inspection.duration.toFixed(2)}s`} />
          )}
          {inspection.error && <Chip size="small" color="error" label="error" />}
        </Stack>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Output" />
          <Tab label="Events" />
          <Tab label="Variables" />
          <Tab label="Context" />
        </Tabs>
        <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
          {tab === 0 && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Node output</Typography>
              <JsonBlock value={inspection.nodeOutput} emptyLabel="No node_outputs entry for this node yet." />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Complete event data</Typography>
              <JsonBlock value={inspection.completePayload} emptyLabel="No node_complete payload captured." />
            </Stack>
          )}
          {tab === 1 && (
            <Stack spacing={1}>
              {inspection.nodeEvents.length === 0 ? (
                <Typography variant="body2" color="text.disabled">No stream events for this node.</Typography>
              ) : (
                inspection.nodeEvents.map((ev, i) => (
                  <Paper key={`${ev.type}-${i}`} variant="outlined" sx={{ p: 1, bgcolor: 'background.default' }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                      <Chip size="small" label={ev.type || ev.langgraph_v2?.event || 'event'} />
                      {ev.langgraph_v2?.event && <Chip size="small" variant="outlined" label={ev.langgraph_v2.event} />}
                    </Stack>
                    <JsonBlock value={ev.data ?? ev.langgraph_v2?.metadata ?? ev} />
                  </Paper>
                ))
              )}
            </Stack>
          )}
          {tab === 2 && <JsonBlock value={inspection.variables} emptyLabel="No variables in run payload." />}
          {tab === 3 && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Start event (pre-node)</Typography>
              <JsonBlock value={inspection.startPayload} emptyLabel="No node_start payload." />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Execution path entry</Typography>
              <JsonBlock value={inspection.pathEntry} emptyLabel="Node not listed in execution_path." />
              {inspection.llmTokens.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>LLM tokens (sample)</Typography>
                  <JsonBlock value={inspection.llmTokens.slice(-20)} />
                </>
              )}
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
